import React, { useState } from 'react';
import { formatDublinDate } from '../../utils/date';
import {
  useListStudentIntakeInvitationsQuery,
  useDeleteIntakeFormMutation,
} from '../../store/api/localApi';

interface Props {
  studentId: string;
  studentName: string;
  isAdmin: boolean;
}

/**
 * StudentDetail "Intake forms" section. Lists the student's STUDENT_INTAKE
 * invitations — completed responses and pending/expired sends — and, for
 * admins, a Delete action (with confirmation) that removes the invitation and
 * any response so the form can be sent again. Hidden entirely when the student
 * has no intake invitations.
 *
 * There is no standalone response-viewer route in the app, so completed rows
 * show status rather than a "View" link.
 */
export const IntakeFormsSection: React.FC<Props> = ({ studentId, studentName, isAdmin }) => {
  const { data, isLoading } = useListStudentIntakeInvitationsQuery({ studentId });
  const [deleteIntake, { isLoading: isDeleting }] = useDeleteIntakeFormMutation();
  const [pendingDelete, setPendingDelete] = useState<{
    formId: string;
    invitationId: string;
  } | null>(null);

  if (isLoading) {
    return (
      <section data-testid="intake-forms-section" className="mt-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Intake forms</h3>
        <p className="text-xs text-gray-500">Loading…</p>
      </section>
    );
  }

  const invitations = data?.invitations ?? [];
  if (invitations.length === 0) return null;

  const completed = invitations.filter((i) => i.status === 'COMPLETED');
  const pending = invitations.filter((i) => i.status !== 'COMPLETED');

  const confirmAndDelete = async () => {
    if (!pendingDelete) return;
    await deleteIntake({ studentId, formId: pendingDelete.formId }).unwrap();
    setPendingDelete(null);
  };

  const renderRow = (inv: (typeof invitations)[number], kind: 'response' | 'pending') => (
    <li
      key={inv.id}
      data-testid={`intake-form-${kind}-${inv.id}`}
      className="flex items-center justify-between px-3 py-2"
    >
      <div>
        <div className="text-sm text-gray-900">{inv.formTitle}</div>
        <div className="text-xs text-gray-500">
          {kind === 'response'
            ? `Completed ${inv.completedAt ? formatDublinDate(inv.completedAt) : ''}`
            : `Sent ${formatDublinDate(inv.createdAt)}`}
          {kind === 'pending' && inv.deadline
            ? ` · due ${formatDublinDate(inv.deadline)}`
            : ''}
          {' · '}
          <span className="uppercase">{inv.status.toLowerCase()}</span>
        </div>
      </div>
      {isAdmin && (
        <button
          type="button"
          data-testid={`intake-form-delete-${inv.id}`}
          onClick={() => setPendingDelete({ formId: inv.formId, invitationId: inv.id })}
          className="text-xs text-red-600 hover:text-red-700"
        >
          Delete
        </button>
      )}
    </li>
  );

  return (
    <section data-testid="intake-forms-section" className="mt-6">
      <h3 className="text-sm font-semibold text-gray-700 mb-2">Intake forms</h3>

      {completed.length > 0 && (
        <ul className="divide-y divide-gray-100 border border-gray-100 rounded">
          {completed.map((inv) => renderRow(inv, 'response'))}
        </ul>
      )}

      {pending.length > 0 && (
        <ul className="divide-y divide-gray-100 border border-gray-100 rounded mt-3">
          {pending.map((inv) => renderRow(inv, 'pending'))}
        </ul>
      )}

      {pendingDelete && (
        <div
          role="dialog"
          aria-modal="true"
          data-testid="intake-form-delete-dialog"
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
        >
          <div className="bg-white rounded-md p-5 max-w-sm w-full shadow-xl">
            <p className="text-sm text-gray-900">
              Delete this intake form for {studentName}? Any response will be removed and you will
              be able to send this form again afterwards.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                data-testid="intake-form-delete-cancel"
                onClick={() => setPendingDelete(null)}
                className="px-3 py-1.5 text-xs border border-gray-300 rounded text-gray-700"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="intake-form-delete-confirm"
                onClick={confirmAndDelete}
                disabled={isDeleting}
                className="px-3 py-1.5 text-xs bg-red-600 text-white rounded disabled:opacity-50"
              >
                {isDeleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
